import { Fragment, useMemo } from "react"

import { Box, useTheme } from "@mui/material"

import { SKELETON_COUNT, SKELETON_INDEX_TO_REF } from "constant"
import { getIcon } from "utils/icon"

import { useTranslation } from "providers/i18n"
import { useDirectory } from "hooks"

import { PageCard, PageCardContent } from "components/card"
import { AppNavigation as DefaultAppNavigation } from "containers/kiosk-h/AppNavigation"
import { DirectoryOccupantCard } from "components/domains/directory/DirectoryOccupantCard"
import { EmptyData } from "components/EmptyData"
import { StoreIcon } from "components/icons"
import { VenueSelector } from "components/VenueSelector"
import {
  CategoryFilterBar,
  CategoryFilterChip,
} from "components/CategoryFilterBar/index.ts"
import { HorizontalScrollPane } from "components/HorizontalScrollPane"
import { DirectorySkeletonCard } from "components/domains/directory"
import { ContentWithAside } from "layouts/kiosk-h"
import { SidebarMenu } from "components/navigation/SidebarMenu"

export const DirectoryKioskHPage = ({
  Layout = ContentWithAside,
  AppNavigation = DefaultAppNavigation,
}) => {
  const { t } = useTranslation()
  const {
    handleChangeSubCategory,
    changeVenueFilter,
    handleChangeParentCategory,
    parentCategoryId,
    sortedParentCategoryWithOccupant,
    filteredOccupantsSorted,
    subCategoryWithActiveParentCategory,
    subCategoryId,
    venueFilter,
    venues,
    isMultiVenueProject,
    locationVenueId,
    hasParentCategory,
    showSubCategoryFilter,
    endScrollRef,
    hasNextPage,
    hasData,
    isFetched,
  } = useDirectory()

  const theme = useTheme()

  const emptyIcon = useMemo(
    () => getIcon(theme?.assets?.empty?.directory) || <StoreIcon />,
    [theme]
  )
  const styleOverrides = theme.components[`DirectoryPage`]?.styleOverrides || {}
  const { venueSelector } = theme.components[`AppMap`]?.styleOverrides || {}
  const {
    pageCard,
    pageCardContent,
    gridContainer,
    gridItem,
    subCategoryFilterBar,
    scrollPane,
    emptyDataWrapper,
    categoryFilterBar,
    filterSection,
  } = styleOverrides

  return (
    <Layout
      aside={
        <PageCard>
          <SidebarMenu AppNavigation={AppNavigation} />
        </PageCard>
      }
    >
      <PageCard sx={pageCard}>
        <PageCardContent sx={pageCardContent}>
          <Box
            sx={gridContainer}
            className={hasParentCategory || hasData ? "" : "emptyList"}
          >
            {hasData ? (
              <>
                {filteredOccupantsSorted.pages.map((page, pageNum) => {
                  return (
                    <Fragment key={pageNum}>
                      {page.data.map((occupant, index) => (
                        <Box
                          sx={gridItem}
                          key={`directory-item-${occupant.id}`}
                        >
                          {/* TODO:: Render collapse item when hasMultipleLocations*/}
                          <DirectoryOccupantCard data={occupant} />
                        </Box>
                      ))}
                    </Fragment>
                  )
                })}
                {hasNextPage &&
                  Array.from({ length: SKELETON_COUNT }).map((_, index) => (
                    <DirectorySkeletonCard
                      ref={
                        index === SKELETON_INDEX_TO_REF ? endScrollRef : null
                      }
                      key={index}
                      sx={gridItem}
                    />
                  ))}
              </>
            ) : !isFetched ? (
              Array.from({ length: SKELETON_COUNT }).map((_, index) => (
                <DirectorySkeletonCard key={index} sx={gridItem} />
              ))
            ) : (
              <Box sx={emptyDataWrapper}>
                <EmptyData
                  icon={emptyIcon}
                  primary={t("Coming soon")}
                  secondary={t("Stay tuned for the update")}
                />
              </Box>
            )}
          </Box>
          {isMultiVenueProject && (
            <Box sx={venueSelector}>
              <VenueSelector
                selectedVenue={venueFilter}
                venues={venues}
                onClickVenue={changeVenueFilter}
                currentVenue={locationVenueId}
              />
            </Box>
          )}
          {hasParentCategory && (
            <Box sx={filterSection}>
              <HorizontalScrollPane
                className="category-filterbar"
                stackProps={{
                  sx: scrollPane,
                }}
              >
                <CategoryFilterBar
                  sx={categoryFilterBar}
                  activeCategory={parentCategoryId}
                  categories={sortedParentCategoryWithOccupant}
                  onChange={handleChangeParentCategory}
                />
              </HorizontalScrollPane>
              {showSubCategoryFilter && (
                <HorizontalScrollPane
                  className="subCategory-filterbar"
                  stackProps={{
                    sx: scrollPane,
                  }}
                >
                  <CategoryFilterBar
                    sx={subCategoryFilterBar}
                    activeCategory={subCategoryId}
                    categories={subCategoryWithActiveParentCategory}
                    onChange={handleChangeSubCategory}
                    renderFilterButton={CategoryFilterChip}
                    enableSeparateBorder={false}
                  />
                </HorizontalScrollPane>
              )}
            </Box>
          )}
        </PageCardContent>
      </PageCard>
    </Layout>
  )
}
